import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AxiosError } from "axios";
import {
  AlertTriangle,
  CalendarRange,
  Clock3,
  Loader2,
  Plane,
  RotateCw,
  Send,
  Trash2,
  UserRoundX,
} from "lucide-react";
import {
  DayPicker,
  type DateRange,
  type DayButtonProps,
} from "react-day-picker";
import { es } from "date-fns/locale";
import type {
  RrhhCalendarDay,
  RrhhVacation,
  RrhhVacationConstraints,
  RrhhVacationSummary,
  VacationJornada,
  VacationOrigin,
} from "../types/rrhh";
import { deleteVacationRequest, requestVacation } from "../api/rrhh";
import "react-day-picker/dist/style.css";

type Props = {
  vacations: RrhhVacation[];
  summary: RrhhVacationSummary;
  calendarDays: RrhhCalendarDay[];
  constraints: RrhhVacationConstraints;
  loading: boolean;
  onReload: () => Promise<void> | void;
};

type Feedback = {
  tone: "success" | "error" | "warning";
  text: string;
} | null;

export function VacationsPanel({
  vacations,
  summary,
  calendarDays,
  constraints,
  loading,
  onReload,
}: Props) {
  const [range, setRange] = useState<DateRange | undefined>();
  const [dayModes, setDayModes] = useState<Record<string, VacationJornada>>({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [hoverInfo, setHoverInfo] = useState<string | null>(null);
  const [activeStatus, setActiveStatus] = useState<
    "todas" | "pendiente" | "aprobado" | "rechazado"
  >("todas");
  const [selectedYear, setSelectedYear] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const calendarMap = useMemo(() => {
    const map = new Map<string, RrhhCalendarDay[]>();

    calendarDays.forEach((entry) => {
      const current = map.get(entry.date) ?? [];
      current.push(entry);
      map.set(entry.date, current);
    });

    return map;
  }, [calendarDays]);

  const today = useMemo(() => parseYmd(constraints.today), [constraints.today]);
  const selectableUntil = useMemo(
    () => parseYmd(constraints.selectable_until),
    [constraints.selectable_until],
  );
  const carryDeadline = useMemo(
    () => parseYmd(constraints.carry_deadline),
    [constraints.carry_deadline],
  );

  const blockedDates = useMemo(
    () =>
      uniqueDates(
        calendarDays
          .filter(
            (entry) =>
              entry.blocked &&
              (entry.status === "pendiente" ||
                entry.status === "aprobado" ||
                entry.status === "partner_blocked" ||
                entry.status === "rest_day"),
          )
          .map((entry) => entry.date),
      ),
    [calendarDays],
  );

  const approvedDates = useMemo(
    () =>
      uniqueDates(
        calendarDays
          .filter((entry) => entry.status === "aprobado")
          .map((entry) => entry.date),
      ),
    [calendarDays],
  );

  const pendingDates = useMemo(
    () =>
      uniqueDates(
        calendarDays
          .filter((entry) => entry.status === "pendiente")
          .map((entry) => entry.date),
      ),
    [calendarDays],
  );

  const rejectedDates = useMemo(
    () =>
      uniqueDates(
        calendarDays
          .filter((entry) => entry.status === "rechazado")
          .map((entry) => entry.date),
      ),
    [calendarDays],
  );

  const partnerDates = useMemo(
    () =>
      uniqueDates(
        calendarDays
          .filter((entry) => entry.status === "partner_blocked")
          .map((entry) => entry.date),
      ),
    [calendarDays],
  );

  const restDates = useMemo(
    () =>
      uniqueDates(
        calendarDays
          .filter((entry) => entry.status === "rest_day")
          .map((entry) => entry.date),
      ),
    [calendarDays],
  );

  const carryWindowDates = useMemo(() => {
    if (summary.carry_days <= 0) return [];

    const start = today > carryDeadline ? carryDeadline : today;
    return iterateDates(start, carryDeadline).filter(
      (date) => !isWeekendDate(date) && date <= selectableUntil,
    );
  }, [carryDeadline, selectableUntil, summary.carry_days, today]);

  const selectedDates = useMemo(
    () => getSelectableDatesFromRange(range, blockedDates, today, selectableUntil),
    [blockedDates, range, selectableUntil, today],
  );

  useEffect(() => {
    setDayModes((current) => {
      if (selectedDates.length === 0) return {};

      const next: Record<string, VacationJornada> = {};

      selectedDates.forEach((date) => {
        next[date] = current[date] ?? "completa";
      });

      return next;
    });
  }, [selectedDates]);

  const requestDays = useMemo(
    () =>
      selectedDates.map((date) => ({
        date,
        jornada: dayModes[date] ?? "completa",
      })),
    [dayModes, selectedDates],
  );

  const availableYears = useMemo(
    () =>
      Array.from(
        new Set(
          vacations.map((vacation) =>
            String(new Date(`${vacation.fecha_inicio}T12:00:00`).getFullYear()),
          ),
        ),
      ).sort((a, b) => Number(b) - Number(a)),
    [vacations],
  );

  const filteredVacations = useMemo(() => {
    return vacations.filter((vacation) => {
      const matchesStatus =
        activeStatus === "todas" || vacation.estado === activeStatus;
      const matchesYear =
        selectedYear === "all" ||
        String(new Date(`${vacation.fecha_inicio}T12:00:00`).getFullYear()) ===
          selectedYear;

      return matchesStatus && matchesYear;
    });
  }, [activeStatus, selectedYear, vacations]);

  const totalPages = Math.max(1, Math.ceil(filteredVacations.length / 5));
  const paginatedVacations = useMemo(
    () => paginateItems(filteredVacations, currentPage, 5),
    [currentPage, filteredVacations],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeStatus, selectedYear]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const selectionPreview = useMemo(
    () => buildSelectionPreview(requestDays, summary, carryDeadline),
    [carryDeadline, requestDays, summary],
  );

  const handleSubmit = async () => {
    if (requestDays.length === 0) return;

    setSaving(true);
    setFeedback(null);

    try {
      const response = await requestVacation(requestDays);
      setFeedback({
        tone: "success",
        text: response.message ?? "Solicitud enviada correctamente.",
      });
      setRange(undefined);
      setDayModes({});
      await onReload();
    } catch (error) {
      if (error instanceof AxiosError) {
        const data = error.response?.data as {
          message?: string;
          errors?: Record<string, string[]>;
        };

        const firstValidationMessage = data?.errors
          ? Object.values(data.errors).flat()[0]
          : null;

        setFeedback({
          tone: "error",
          text:
            firstValidationMessage ??
            data?.message ??
            "No se pudo enviar la solicitud.",
        });
      } else {
        setFeedback({
          tone: "error",
          text: "No se pudo enviar la solicitud.",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    setFeedback(null);

    try {
      const response = await deleteVacationRequest(id);
      setFeedback({
        tone: "success",
        text: response.message ?? "Solicitud eliminada.",
      });
      await onReload();
    } catch (error) {
      if (error instanceof AxiosError) {
        setFeedback({
          tone: "error",
          text:
            (error.response?.data as { message?: string })?.message ??
            "No se pudo eliminar la solicitud.",
        });
      } else {
        setFeedback({
          tone: "error",
          text: "No se pudo eliminar la solicitud.",
        });
      }
    } finally {
      setDeletingId(null);
    }
  };

  const DayButtonWithTooltip = (props: DayButtonProps) => {
    const iso = toYmd(props.day.date);
    const entries = calendarMap.get(iso) ?? [];
    const title = entries.map((entry) => entry.tooltip).join(" • ");

    return (
      <button
        {...props}
        title={title || undefined}
        onMouseEnter={(event) => {
          props.onMouseEnter?.(event);
          setHoverInfo(title || null);
        }}
        onMouseLeave={(event) => {
          props.onMouseLeave?.(event);
          setHoverInfo(null);
        }}
      />
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="rrhh-panel-header">
        <div className="relative flex items-center gap-4">
          <span className="rrhh-panel-icon">
            <Plane size={22} />
          </span>

          <div>
            <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-600">
              Gestión avanzada
            </div>

            <h2 className="mt-1 text-2xl font-black tracking-tight text-slate-950">
              Vacaciones
            </h2>

            <p className="mt-1 text-sm font-semibold leading-6 text-slate-500">
              Selección visual por rango, carry automático, medios días y bloqueos por compatibilidad.
            </p>

            <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2">
                {[
                  ["todas", "Todas"],
                  ["pendiente", "Pendientes"],
                  ["aprobado", "Aprobadas"],
                  ["rechazado", "Rechazadas"],
                ].map(([status, label]) => {
                  const active = activeStatus === status;

                  return (
                    <button
                      key={status}
                      type="button"
                      onClick={() =>
                        setActiveStatus(
                          status as "todas" | "pendiente" | "aprobado" | "rechazado",
                        )
                      }
                      className={[
                        "rounded-full border px-4 py-2 text-xs font-black transition-all duration-300",
                        active
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700 shadow-[0_8px_18px_rgba(16,185,129,.08)]"
                          : "border-slate-200 bg-white text-slate-500 hover:-translate-y-0.5 hover:border-emerald-200 hover:text-slate-900",
                      ].join(" ")}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs font-black uppercase tracking-[0.14em] text-slate-400">
                  Año
                </span>
                <select
                  value={selectedYear}
                  onChange={(event) => setSelectedYear(event.target.value)}
                  className="rounded-2xl border border-[#e7ecef] bg-white px-3 py-2 text-sm font-semibold text-slate-600 shadow-[0_8px_18px_rgba(15,23,42,.03)]"
                >
                  <option value="all">Todos</option>
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => void onReload()}
            disabled={loading || saving}
            className="rrhh-soft-button ml-auto shrink-0 self-start"
          >
            <RotateCw size={15} className={loading ? "animate-spin" : ""} />
            Actualizar
          </button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-5 overflow-auto p-5 xl:grid-cols-[minmax(0,1.2fr)_360px]">
        <section className="rrhh-users-gradient-card rrhh-soft-gradient-card p-5">
          <div className="rrhh-calendar-card rounded-[28px] border border-[#edf1f3] bg-white p-4 shadow-[0_10px_28px_rgba(15,23,42,.04)]">
            <DayPicker
              mode="range"
              selected={range}
              onSelect={setRange}
              locale={es}
              weekStartsOn={1}
              numberOfMonths={2}
              disabled={[
                { before: today },
                { after: selectableUntil },
                { dayOfWeek: [0, 6] },
                ...blockedDates.map(parseYmd),
              ]}
              modifiers={{
                approved: approvedDates.map(parseYmd),
                pending: pendingDates.map(parseYmd),
                rejected: rejectedDates.map(parseYmd),
                partner: partnerDates.map(parseYmd),
                restday: restDates.map(parseYmd),
                carrywindow: carryWindowDates,
              }}
              modifiersClassNames={{
                approved: "rrhh-day-approved",
                pending: "rrhh-day-pending",
                rejected: "rrhh-day-rejected",
                partner: "rrhh-day-partner",
                restday: "bg-sky-100 !text-sky-800 ring-1 ring-inset ring-sky-300",
                carrywindow: "rrhh-day-carry-window",
              }}
              onDayMouseEnter={(day) => {
                const title = (calendarMap.get(toYmd(day)) ?? [])
                  .map((entry) => entry.tooltip)
                  .join(" • ");
                setHoverInfo(title || null);
              }}
              onDayMouseLeave={() => setHoverInfo(null)}
              components={{ DayButton: DayButtonWithTooltip }}
              className="rrhh-calendar"
              style={{ width: "100%" }}
            />
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-[1.1fr_.9fr]">
            <div className="rounded-[26px] border border-[#edf1f3] bg-[linear-gradient(180deg,#ffffff_0%,#f8fcfa_100%)] p-4 shadow-[0_8px_22px_rgba(15,23,42,.035)]">
              <div className="text-xs font-black uppercase tracking-[0.14em] text-emerald-700">
                Leyenda visual
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Legend label="Aprobadas" className="bg-emerald-100 text-emerald-700" />
                <Legend label="Descanso plantilla" className="bg-sky-100 text-sky-700" />
                <Legend label="Pendientes" className="bg-amber-100 text-amber-700" />
                <Legend label="Rechazadas" className="bg-rose-100 text-rose-600" />
                <Legend label="Pareja bloqueada" className="bg-violet-100 text-violet-700" />
                <Legend label="Ventana carry" className="bg-sky-100 text-sky-700" />
              </div>
            </div>

            <div className="rounded-[26px] border border-[#edf1f3] bg-[linear-gradient(180deg,#ffffff_0%,#f8fcfa_100%)] p-4 shadow-[0_8px_22px_rgba(15,23,42,.035)]">
              <div className="text-xs font-black uppercase tracking-[0.14em] text-slate-400">
                Hover info
              </div>
              <div className="mt-2 min-h-[56px] text-sm font-semibold leading-6 text-slate-600">
                {hoverInfo ??
                  "Pasa el cursor por una fecha para ver estados, rechazos o bloqueos de compañero."}
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4">
            <div className="flex items-center justify-between rounded-[28px] border border-[#edf1f3] bg-white/90 px-4 py-4 shadow-[0_8px_22px_rgba(15,23,42,.035)]">
              <div>
                <div className="text-xs font-black uppercase tracking-[0.14em] text-emerald-700">
                  Rango seleccionado
                </div>
                <div className="mt-1 text-sm font-semibold text-slate-500">
                  {requestDays.length === 0
                    ? "Selecciona fechas laborales disponibles."
                    : `${requestDays.length} fecha(s) preparadas para la solicitud.`}
                </div>
              </div>

              <div className="text-right">
                <div className="text-3xl font-black text-slate-950">
                  {formatDays(selectionPreview.totalDays)}
                </div>
                <div className="text-xs font-black text-slate-400">días</div>
              </div>
            </div>

            <div className="grid gap-3">
              {requestDays.length === 0 ? (
                <div className="rrhh-empty-state">
                  Selecciona un rango para asignar jornada completa, mañana o tarde por cada día.
                </div>
              ) : (
                requestDays.map((day) => {
                  const allocation = selectionPreview.breakdown[day.date];

                  return (
                    <div
                      key={day.date}
                      className="grid gap-3 rounded-[28px] border border-[#edf1f3] bg-white/92 p-4 shadow-[0_8px_22px_rgba(15,23,42,.035)] md:grid-cols-[1fr_auto_auto]"
                    >
                      <div>
                        <div className="text-sm font-black text-slate-950">
                          {formatLongDate(day.date)}
                        </div>
                        <div className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
                          {allocation.originLabel}
                        </div>
                      </div>

                      <div className="grid grid-cols-3 rounded-[20px] border border-[#e7ecef] bg-slate-50 p-1">
                        {(["completa", "manana", "tarde"] as VacationJornada[]).map(
                          (option) => (
                            <button
                              key={option}
                              type="button"
                              onClick={() =>
                                setDayModes((current) => ({
                                  ...current,
                                  [day.date]: option,
                                }))
                              }
                              className={[
                                "rounded-2xl px-3 py-2 text-xs font-black transition-all duration-300",
                                (dayModes[day.date] ?? "completa") === option
                                  ? "bg-white text-emerald-700 shadow-[0_8px_18px_rgba(16,185,129,.10)]"
                                  : "text-slate-500 hover:text-slate-900",
                              ].join(" ")}
                            >
                              {formatJornada(option)}
                            </button>
                          ),
                        )}
                      </div>

                      <div className="flex items-center justify-end">
                        <span
                          className={`inline-flex rounded-full border px-3 py-1.5 text-xs font-black ${originTone(
                            allocation.origin,
                          )}`}
                        >
                          {allocation.originLabel}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </section>

        <aside className="rrhh-users-gradient-card rrhh-soft-gradient-card flex flex-col gap-4 p-5">
          <div className="rounded-[28px] border border-[#edf1f3] bg-white p-4 text-center shadow-[0_10px_28px_rgba(15,23,42,.045)]">
            <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
              Saldo total disponible
            </div>
            <div className="mt-3 text-5xl font-black tracking-tight text-slate-950">
              {formatDays(summary.remaining_days)}
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-500">
              Suma de vacaciones actuales y carry utilizable.
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Actuales" value={formatDays(summary.remaining_current_days)} />
            <StatCard
              label="Carry"
              value={formatDays(summary.remaining_carry_days)}
              tone={summary.carry_locked ? "warning" : "neutral"}
            />
            <StatCard label="Medios días" value={`${summary.half_days_remaining}/${summary.max_half_days}`} />
          </div>

          <div className="rounded-[28px] border border-[#edf1f3] bg-[linear-gradient(180deg,#ffffff_0%,#f8fcfa_100%)] p-4 shadow-[0_10px_28px_rgba(15,23,42,.045)]">
            <div className="text-xs font-black uppercase tracking-[0.14em] text-emerald-700">
              Preview de consumo
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2">
              <MiniStat label="Seleccionado" value={formatDays(selectionPreview.totalDays)} />
              <MiniStat label="Carry" value={formatDays(selectionPreview.carryDays)} />
              <MiniStat label="Actual" value={formatDays(selectionPreview.currentDays)} />
            </div>

            <div className="mt-3 text-sm font-semibold leading-6 text-slate-500">
              {summary.carry_locked
                ? `El carry de ${summary.carry_origin_year} quedó bloqueado al superar el ${formatShortDate(summary.carry_deadline)}.`
                : `El carry de ${summary.carry_origin_year} se consume primero hasta el ${formatShortDate(summary.carry_deadline)}.`}
            </div>
          </div>

          {summary.half_days_remaining === 0 && (
            <NoticeBox
              icon={<Clock3 size={16} />}
              tone="warning"
              text="Has alcanzado el máximo anual de medios días. Solo podrás seleccionar jornadas completas."
            />
          )}

          {summary.carry_locked && summary.carry_days > 0 && (
            <NoticeBox
              icon={<AlertTriangle size={16} />}
              tone="warning"
              text={`Quedan ${formatDays(summary.carry_days)} de carry histórico, pero ya no es posible usarlos este año.`}
            />
          )}

          {partnerDates.length > 0 && (
            <NoticeBox
              icon={<UserRoundX size={16} />}
              tone="partner"
              text="Las fechas moradas quedan bloqueadas por vacaciones pendientes o aprobadas de tu compañero vinculado."
            />
          )}

          {feedback && <FeedbackBox feedback={feedback} />}

          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving || requestDays.length === 0}
            className="rrhh-primary-button w-full disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:scale-100"
          >
            {saving ? (
              <Loader2 size={17} className="animate-spin" />
            ) : (
              <Send size={17} />
            )}
            Solicitar vacaciones
          </button>
        </aside>

        <div className="dashboard-scale-in min-h-[260px] overflow-hidden rounded-[34px] border border-[#d7e5df] bg-white shadow-[0_24px_70px_rgba(15,23,42,.10)] backdrop-blur-xl xl:col-span-2">
          <div className="border-b border-[#edf1f3] bg-[linear-gradient(135deg,#ffffff,#f4fbf8)] p-5">
            <h3 className="flex items-center gap-3 text-lg font-black text-slate-950">
              <span className="grid h-10 w-10 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700">
                <CalendarRange size={18} />
              </span>
              Solicitudes
            </h3>

            <p className="mt-2 text-sm font-semibold text-slate-500">
              Histórico de vacaciones con carry, medios días, rechazos y detalle por jornada.
            </p>
          </div>

          {loading ? (
            <div className="p-5 text-sm font-bold text-slate-400">
              Cargando vacaciones...
            </div>
          ) : filteredVacations.length === 0 ? (
            <div className="p-5 text-sm font-bold text-slate-400">
              No hay solicitudes para los filtros seleccionados.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full min-w-[980px] border-separate border-spacing-0">
                <thead>
                  <tr>
                    {[
                      "Periodo",
                      "Estado",
                      "Días",
                      "Saldo",
                      "Detalle",
                      "Solicitud",
                      "Acciones",
                    ].map((header) => (
                      <th
                        key={header}
                        className="border-b border-[#edf1f3] bg-slate-50/80 px-5 py-4 text-left text-xs font-black uppercase tracking-[0.14em] text-slate-400"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {paginatedVacations.map((vacation) => (
                    <tr
                      key={vacation.id}
                      className="group transition-all duration-300 hover:scale-[1.002]"
                    >
                      <td className="border-b border-[#edf1f3] px-5 py-4 align-top transition-all duration-300 group-hover:bg-emerald-50/45">
                        <div className="font-black text-slate-950">
                          {formatShortDate(vacation.fecha_inicio)}
                        </div>
                        <div className="mt-1 text-sm font-semibold text-slate-500">
                          hasta {formatShortDate(vacation.fecha_fin)}
                        </div>
                      </td>

                      <td className="border-b border-[#edf1f3] px-5 py-4 align-top transition-all duration-300 group-hover:bg-emerald-50/45">
                        <StatusBadge status={vacation.estado} />
                        {vacation.estado === "rechazado" && (
                          <div className="mt-3 rounded-2xl border border-rose-100 bg-rose-50/80 p-3 text-xs font-semibold leading-5 text-rose-700">
                            <div className="font-black">
                              {vacation.motivo_rechazo ?? "Motivo no indicado"}
                            </div>
                            {vacation.comentario_rechazo && (
                              <div className="mt-1">
                                {vacation.comentario_rechazo}
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      <td className="border-b border-[#edf1f3] px-5 py-4 align-top text-sm font-black text-emerald-700 transition-all duration-300 group-hover:bg-emerald-50/45">
                        {formatDays(vacation.dias_solicitados)}
                      </td>

                      <td className="border-b border-[#edf1f3] px-5 py-4 align-top transition-all duration-300 group-hover:bg-emerald-50/45">
                        <div className="flex flex-wrap gap-2">
                          <Legend
                            label={`Actual ${formatDays(vacation.dias_actuales)}`}
                            className="bg-emerald-50 text-emerald-700"
                          />
                          <Legend
                            label={`Carry ${formatDays(vacation.dias_carry)}`}
                            className="bg-sky-50 text-sky-700"
                          />
                        </div>
                      </td>

                      <td className="border-b border-[#edf1f3] px-5 py-4 align-top transition-all duration-300 group-hover:bg-emerald-50/45">
                        <div className="flex flex-wrap gap-2">
                          {vacationDetailBadges(vacation).map((detail) => (
                            <span
                              key={`${vacation.id}-${detail.key}`}
                              className={`inline-flex rounded-full border px-3 py-1 text-xs font-black ${originTone(
                                detail.origin,
                              )}`}
                            >
                              {detail.label}
                            </span>
                          ))}
                        </div>
                      </td>

                      <td className="border-b border-[#edf1f3] px-5 py-4 align-top text-sm font-semibold text-slate-500 transition-all duration-300 group-hover:bg-emerald-50/45">
                        {formatDateTime(vacation.fecha_solicitud)}
                      </td>

                      <td className="border-b border-[#edf1f3] px-5 py-4 align-top transition-all duration-300 group-hover:bg-emerald-50/45">
                        {vacation.estado === "pendiente" ? (
                          <button
                            type="button"
                            onClick={() => void handleDelete(vacation.id)}
                            disabled={deletingId === vacation.id}
                            className="inline-flex items-center gap-1 rounded-full border border-rose-100 bg-rose-50 px-3 py-1.5 text-xs font-black text-rose-600 shadow-[0_8px_20px_rgba(244,63,94,.06)] transition-all duration-300 hover:-translate-y-1 hover:bg-rose-100 disabled:opacity-50"
                          >
                            {deletingId === vacation.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Trash2 size={14} />
                            )}
                            Eliminar
                          </button>
                        ) : (
                          <span className="text-sm font-bold text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-[#edf1f3] bg-slate-50/70 px-5 py-4">
                  <div className="text-sm font-semibold text-slate-500">
                    Página {currentPage} de {totalPages}
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={currentPage === 1}
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-600 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage((page) => Math.min(totalPages, page + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-600 disabled:opacity-50"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "warning";
}) {
  return (
    <div
      className={[
        "rounded-2xl border p-3 shadow-[0_8px_20px_rgba(15,23,42,.035)] transition-all duration-300 hover:-translate-y-0.5",
        tone === "warning"
          ? "border-amber-100 bg-amber-50/70"
          : "border-[#edf1f3] bg-[linear-gradient(180deg,#ffffff_0%,#f8fcfa_100%)]",
      ].join(" ")}
    >
      <div className="text-xs font-black uppercase tracking-[0.14em] text-slate-400">
        {label}
      </div>
      <div className="mt-1 text-lg font-black text-slate-950">{value}</div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[#edf1f3] bg-white px-3 py-3 text-center shadow-[0_8px_18px_rgba(15,23,42,.03)]">
      <div className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-400">
        {label}
      </div>
      <div className="mt-1 text-lg font-black text-slate-950">{value}</div>
    </div>
  );
}

function Legend({ label, className }: { label: string; className: string }) {
  return (
    <span
      className={`inline-flex rounded-full border border-transparent px-3 py-1 text-xs font-black ${className}`}
    >
      {label}
    </span>
  );
}

function NoticeBox({
  icon,
  text,
  tone,
}: {
  icon: ReactNode;
  text: string;
  tone: "warning" | "partner";
}) {
  const toneClass =
    tone === "warning"
      ? "border-amber-100 bg-amber-50 text-amber-700"
      : "border-violet-100 bg-violet-50 text-violet-700";

  return (
    <div className={`rounded-[24px] border px-4 py-3 text-sm font-semibold leading-6 ${toneClass}`}>
      <div className="inline-flex items-center gap-2 font-black">{icon} Aviso</div>
      <div className="mt-2">{text}</div>
    </div>
  );
}

function FeedbackBox({ feedback }: { feedback: Feedback }) {
  if (!feedback) return null;

  const toneClass =
    feedback.tone === "success"
      ? "border-emerald-100 bg-emerald-50 text-emerald-700"
      : feedback.tone === "warning"
        ? "border-amber-100 bg-amber-50 text-amber-700"
        : "border-rose-100 bg-rose-50 text-rose-600";

  return (
    <div className={`rounded-2xl border px-4 py-3 text-sm font-bold ${toneClass}`}>
      {feedback.text}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "aprobado"
      ? "border-emerald-100 bg-emerald-50 text-emerald-700"
      : status === "rechazado"
        ? "border-rose-100 bg-rose-50 text-rose-600"
        : "border-amber-100 bg-amber-50 text-amber-600";

  return (
    <span
      className={`inline-flex rounded-full border px-3 py-1 text-xs font-black transition-all duration-300 hover:-translate-y-0.5 ${cls}`}
    >
      {status}
    </span>
  );
}

function buildSelectionPreview(
  days: Array<{ date: string; jornada: VacationJornada }>,
  summary: RrhhVacationSummary,
  carryDeadline: Date,
) {
  const breakdown: Record<
    string,
    { origin: VacationOrigin; originLabel: string }
  > = {};

  let carryRemaining = summary.carry_locked ? 0 : summary.remaining_carry_days;
  let carryDays = 0;
  let currentDays = 0;
  let totalDays = 0;

  const slots = days
    .flatMap((day) => expandDayToSlots(day))
    .sort((a, b) => a.date.localeCompare(b.date) || a.slot.localeCompare(b.slot));

  const originByDate = new Map<string, Set<VacationOrigin>>();

  slots.forEach((slot) => {
    const isCarryEligible = parseYmd(slot.date) <= carryDeadline && carryRemaining >= slot.amount;
    const origin: VacationOrigin = isCarryEligible ? "carry" : "actual";

    if (isCarryEligible) {
      carryRemaining -= slot.amount;
      carryDays += slot.amount;
    } else {
      currentDays += slot.amount;
    }

    totalDays += slot.amount;
    const currentOrigins = originByDate.get(slot.date) ?? new Set<VacationOrigin>();
    currentOrigins.add(origin);
    originByDate.set(slot.date, currentOrigins);
  });

  days.forEach((day) => {
    const origins = originByDate.get(day.date) ?? new Set<VacationOrigin>(["actual"]);
    const origin =
      origins.size > 1 ? "mixto" : (Array.from(origins)[0] as VacationOrigin);

    breakdown[day.date] = {
      origin,
      originLabel:
        origin === "carry"
          ? "Consume carry"
          : origin === "mixto"
            ? "Mixto carry/actual"
            : "Consume actual",
    };
  });

  return {
    totalDays,
    carryDays,
    currentDays,
    breakdown,
  };
}

function getSelectableDatesFromRange(
  range: DateRange | undefined,
  blockedDates: string[],
  today: Date,
  selectableUntil: Date,
) {
  if (!range?.from) return [];

  const end = range.to ?? range.from;
  const blocked = new Set(blockedDates);

  return iterateDates(range.from, end)
    .filter((date) => !isWeekendDate(date))
    .filter((date) => date >= today && date <= selectableUntil)
    .map(toYmd)
    .filter((date) => !blocked.has(date));
}

function expandDayToSlots(day: { date: string; jornada: VacationJornada }) {
  if (day.jornada === "completa") {
    return [
      { date: day.date, slot: "manana", amount: 0.5 },
      { date: day.date, slot: "tarde", amount: 0.5 },
    ];
  }

  return [{ date: day.date, slot: day.jornada, amount: 0.5 }];
}

function vacationDetailBadges(vacation: RrhhVacation) {
  if (
    vacation.dias.length > 1 &&
    vacation.dias.every((day) => day.jornada === "completa")
  ) {
    return [
      {
        key: `range-${vacation.fecha_inicio}-${vacation.fecha_fin}`,
        label: `${formatShortDate(vacation.fecha_inicio)} al ${formatShortDate(vacation.fecha_fin)} · Completa`,
        origin: vacation.saldo_origen,
      },
    ];
  }

  return vacation.dias.map((day) => ({
    key: `${day.date}-${day.jornada}`,
    label: `${formatShortDate(day.date)} · ${formatJornada(day.jornada)}`,
    origin: day.saldo_origen,
  }));
}

function originTone(origin: VacationOrigin) {
  if (origin === "carry") return "border-sky-100 bg-sky-50 text-sky-700";
  if (origin === "mixto") return "border-fuchsia-100 bg-fuchsia-50 text-fuchsia-700";
  return "border-emerald-100 bg-emerald-50 text-emerald-700";
}

function formatJornada(value: VacationJornada) {
  if (value === "manana") return "Mañana";
  if (value === "tarde") return "Tarde";
  return "Completa";
}

function formatDays(value: number) {
  const normalized = Math.round(value * 10) / 10;
  return Number.isInteger(normalized) ? String(normalized) : normalized.toFixed(1);
}

function parseYmd(value: string) {
  return new Date(`${value}T12:00:00`);
}

function toYmd(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function iterateDates(start: Date, end: Date) {
  const dates: Date[] = [];
  const current = new Date(start);
  current.setHours(12, 0, 0, 0);
  const last = new Date(end);
  last.setHours(12, 0, 0, 0);

  while (current <= last) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

function isWeekendDate(date: Date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

function uniqueDates(values: string[]) {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
}

function paginateItems<T>(items: T[], page: number, pageSize: number) {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

function formatShortDate(value: string) {
  return new Date(`${value}T12:00:00`).toLocaleDateString("es-ES");
}

function formatLongDate(value: string) {
  return new Date(`${value}T12:00:00`).toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString("es-ES");
}
